import React, { useEffect } from "react";
import { Maximize2, Minimize2, MessageCirclePlus } from "lucide-react";
import { WidgetWrapper } from "../../../../components/WidgetWrapper/WidgetWrapper";
import { EditField } from "../../../../components/EditPanel/types";
import { useWidgetEditable } from "../../../../hooks/useWidgetEditable";
import { themeColor } from "../../../../types/color";
import {
  ConciergeHeaderWidgetProps,
  ConciergeHeaderWidgetState,
} from "./types";

/** Close (X) icon - 16x16, matches Concierge header mock */
const CloseIcon: React.FC<{ className?: string }> = ({ className }) => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    width="16"
    height="16"
    viewBox="0 0 16 16"
    fill="none"
    className={className}
  >
    <g clipPath="url(#clip0_concierge_close)">
      <path
        fillRule="evenodd"
        clipRule="evenodd"
        d="M9.23815 8.00015L14.7435 2.49459C15.0855 2.15283 15.0855 1.5983 14.7435 1.25654C14.4014 0.914486 13.8475 0.914486 13.5055 1.25654L8.00015 6.7621L2.49453 1.25654C2.15249 0.914486 1.59858 0.914486 1.25653 1.25654C0.914489 1.5983 0.914489 2.15283 1.25653 2.49459L6.76215 8.00015L1.25653 13.5057C0.914489 13.8475 0.914489 14.402 1.25653 14.7438C1.42755 14.9145 1.65168 15 1.87553 15C2.09938 15 2.32351 14.9145 2.49453 14.7435L8.00015 9.2379L13.5055 14.7435C13.6765 14.9145 13.9006 15 14.1245 15C14.3483 15 14.5725 14.9145 14.7435 14.7435C15.0855 14.4017 15.0855 13.8472 14.7435 13.5054L9.23815 8.00015Z"
        fill="currentColor"
        stroke="currentColor"
        strokeWidth="0.5"
      />
    </g>
    <defs>
      <clipPath id="clip0_concierge_close">
        <rect width="16" height="16" fill="white" />
      </clipPath>
    </defs>
  </svg>
);

/**
 * Load a Google Font stylesheet into document.head.
 * This is necessary because fonts loaded inside Shadow DOM don't register
 * @font-face declarations globally, causing the font to not render.
 */
const loadGoogleFontInHead = (fontUrl: string): void => {
  // Check if already loaded
  const existingLink = document.querySelector(`link[href="${fontUrl}"]`);
  if (existingLink) return;

  // Inject into document.head (works with Shadow DOM)
  const link = document.createElement("link");
  link.rel = "stylesheet";
  link.href = fontUrl;
  document.head.appendChild(link);
};

const defaultState: ConciergeHeaderWidgetState = {
  // Text content
  titleContent: "CONCIERGE",
  titleFontSize: 16,
  titleFontWeight: "bold",
  titleFontFamily: "inherit",
  titleColor: themeColor("textPrimary"),
  titleLetterSpacing: 2,
  titleTextTransform: "uppercase",
  titleTextAlign: "center",
  titleLineHeight: 1.0,

  // Button colors - default to textPrimary theme color
  closeButtonColor: themeColor("textPrimary"),
  maximizeButtonColor: themeColor("textPrimary"),
  historyButtonColor: themeColor("textPrimary"),
};

const editFields: EditField[] = [
  // Text content
  { key: "titleContent", label: "Title Text", type: "text" },
  {
    key: "titleFontSize",
    label: "Title Font Size (px)",
    type: "number",
    min: 10,
    max: 24,
    step: 1,
  },
  {
    key: "titleFontWeight",
    label: "Title Font Weight",
    type: "options",
    options: [
      { label: "Normal", value: "normal" },
      { label: "Medium", value: "medium" },
      { label: "Semibold", value: "semibold" },
      { label: "Bold", value: "bold" },
    ],
  },
  {
    key: "titleFontFamily",
    label: "Title Font Family",
    type: "font",
  },
  { key: "titleColor", label: "Title Color", type: "color" },
  {
    key: "titleLetterSpacing",
    label: "Title Letter Spacing (px)",
    type: "number",
    min: -2,
    max: 5,
    step: 0.5,
  },
  {
    key: "titleTextTransform",
    label: "Title Text Transform",
    type: "options",
    options: [
      { label: "None", value: "none" },
      { label: "Uppercase", value: "uppercase" },
      { label: "Lowercase", value: "lowercase" },
      { label: "Capitalize", value: "capitalize" },
    ],
  },
  {
    key: "titleTextAlign",
    label: "Title Text Alignment",
    type: "options",
    options: [
      { label: "Left", value: "left" },
      { label: "Center", value: "center" },
      { label: "Right", value: "right" },
    ],
  },
  {
    key: "titleLineHeight",
    label: "Title Line Height",
    type: "number",
    min: 0.8,
    max: 2.0,
    step: 0.1,
  },

  // Button colors
  { key: "closeButtonColor", label: "Close Button Color", type: "color" },
  {
    key: "maximizeButtonColor",
    label: "Maximize Button Color",
    type: "color",
  },
  {
    key: "historyButtonColor",
    label: "History Button Color",
    type: "color",
  },
];

const getFontWeightValue = (weight: string): number => {
  const weightMap: Record<string, number> = {
    normal: 400,
    medium: 500,
    semibold: 600,
    bold: 700,
  };
  return weightMap[weight] || 400;
};

export const ConciergeHeaderWidget: React.FC<ConciergeHeaderWidgetProps> = ({
  showMaximize = true,
  isMaximized = false,
  onMaximize,
  onMinimize,
  onClose,
  onHistoryClick: _onHistoryClick,
  onNewConversationClick,
  showNewConversation = true,
  newConversationIcon,
  title = "CONCIERGE",
  titleAddon,
  widgetId = "concierge-widget-header",
  displayName = "Concierge Header",
  initialState = {},
  // mobileBrandLogoUrl,
  // showMobileLogo = false,
  bottomContent,
}) => {
  const {
    state,
    isLoading,
    isSaving,
    handleEditChange,
    handleSave,
    themeColors,
    themeFonts,
    resolveColor,
    canSave,
  } = useWidgetEditable<ConciergeHeaderWidgetState>(
    widgetId,
    {
      ...defaultState,
      titleContent: title?.toUpperCase() || defaultState.titleContent,
      ...initialState,
    },
    editFields
  );

  // Resolve font family - if "inherit", use inherit; otherwise look up from themeFonts
  const fontKey = state.titleFontFamily;
  const fontData =
    fontKey && fontKey !== "inherit" ? themeFonts[fontKey] : null;

  // For Google Fonts, we need to load via <link> tag in document.head
  const googleFontLink =
    fontData && fontData.format === "google" ? fontData.url : null;

  // Load Google Font into document.head when font changes
  useEffect(() => {
    if (googleFontLink) {
      loadGoogleFontInHead(googleFontLink);
    }
  }, [googleFontLink]);

  // Show loading state while fetching theme config
  if (isLoading) {
    return (
      <div
        className="tw:flex tw:items-center tw:justify-between tw:p-4"
        data-concierge-header
      />
    );
  }

  // Determine font-family CSS based on font type
  const fontFamilyCss = fontData
    ? fontData.format === "system"
      ? fontData.url
      : `"${fontData.name}", sans-serif`
    : "inherit";

  // Determine if we need @font-face (only for uploaded fonts)
  const needsFontFace =
    fontData && fontData.format !== "system" && fontData.format !== "google";

  // Generate unique CSS class for this widget instance
  const widgetClass = `concierge-header-${widgetId || "default"}`;

  // Calculate hover color (70% opacity of base color) for CSS
  const getHoverColor = (color: string): string => {
    // If color is hex, convert to rgba with 70% opacity
    if (color.startsWith("#")) {
      const hex = color.slice(1);
      // Handle both 3-digit and 6-digit hex
      const r =
        hex.length === 3
          ? parseInt(hex[0] + hex[0], 16)
          : parseInt(hex.slice(0, 2), 16);
      const g =
        hex.length === 3
          ? parseInt(hex[1] + hex[1], 16)
          : parseInt(hex.slice(2, 4), 16);
      const b =
        hex.length === 3
          ? parseInt(hex[2] + hex[2], 16)
          : parseInt(hex.slice(4, 6), 16);
      return `rgba(${r}, ${g}, ${b}, 0.7)`;
    }
    // If already rgba, try to modify opacity
    if (color.startsWith("rgba")) {
      return color.replace(/,\s*[\d.]+\)$/, ", 0.7)");
    }
    // If rgb, convert to rgba
    if (color.startsWith("rgb")) {
      return color.replace("rgb", "rgba").replace(")", ", 0.7)");
    }
    // Fallback: return as-is
    return color;
  };

  const widgetContent = (
    <div
      style={{
        borderBottom: "1px solid #F3F3F3",
        boxShadow: "8px 8px 14px 0 rgba(0, 0, 0, 0.03)",
        backgroundColor: "#ffffff",
      }}
    >
      <style>{`
        ${
          needsFontFace
            ? `
          @font-face {
            font-family: "${fontData.name}";
            src: url("${fontData.url}") format("${fontData.format}");
            font-display: swap;
          }
        `
            : ""
        }
        ${
          fontFamilyCss !== "inherit"
            ? `.${widgetClass} [data-title-text] { font-family: ${fontFamilyCss} !important; }`
            : ""
        }
        .${widgetClass} .concierge-maximize-icon {
          color: ${resolveColor(state.maximizeButtonColor)};
          transition: color 0.2s ease;
        }
        .${widgetClass} .concierge-maximize-icon:hover {
          color: ${getHoverColor(resolveColor(state.maximizeButtonColor))};
        }
        .${widgetClass} .concierge-close-icon {
          color: ${resolveColor(state.closeButtonColor)};
          transition: color 0.2s ease;
        }
        .${widgetClass} .concierge-close-icon:hover {
          color: ${getHoverColor(resolveColor(state.closeButtonColor))};
        }
        .${widgetClass} .concierge-history-icon {
          color: ${resolveColor(state.historyButtonColor)};
          transition: color 0.2s ease;
        }
        .${widgetClass} .concierge-history-icon:hover {
          color: ${getHoverColor(resolveColor(state.historyButtonColor))};
        }
        .${widgetClass} .concierge-new-conversation-icon {
          color: ${resolveColor(state.historyButtonColor)};
          transition: color 0.2s ease;
        }
        .${widgetClass} .concierge-new-conversation-icon:hover {
          color: ${getHoverColor(resolveColor(state.historyButtonColor))};
        }
        /* A touch screen gets buttons of 40 x 40 px. The header keeps its height. */
        @media (pointer: coarse) {
          .${widgetClass} button { width: 40px; height: 40px; }
          .${widgetClass} { padding-top: 12px !important; padding-bottom: 12px !important; }
        }
        /* A very narrow screen, such as a phone at 200% zoom, drops Expand, so Close stays in view. */
        @media (max-width: 239px) {
          .${widgetClass} .concierge-header-side { min-width: 0 !important; }
          .${widgetClass} .concierge-header-expand { display: none !important; }
        }
      `}</style>
      <div
        className={`tw:flex tw:items-center tw:justify-between tw:min-h-[44px] ${widgetClass}`}
        style={{ padding: 16 }}
        data-concierge-header
      >
        {/* Left side: expand/minimize, create new conversation — 0 gap, 8px button padding */}
        <div
          className="tw:flex tw:items-center tw:gap-0 tw:flex-shrink-0 concierge-header-side"
          style={{ minWidth: "80px" }}
        >
          {isMaximized && onMinimize && (
            <button
              onClick={onMinimize}
              className="tw:flex tw:items-center tw:justify-center tw:rounded-lg tw:transition-colors tw:cursor-pointer tw:border-none tw:bg-transparent tw:group tw:w-8 tw:h-8"
              style={{ padding: 8 }}
              aria-label="Minimize to widget"
            >
              <Minimize2 className="tw:w-4 tw:h-4 tw:shrink-0 concierge-maximize-icon" />
            </button>
          )}
          {!isMaximized && showMaximize && onMaximize && (
            <button
              onClick={onMaximize}
              className="tw:flex tw:items-center tw:justify-center tw:rounded-lg tw:transition-colors tw:cursor-pointer tw:border-none tw:bg-transparent tw:group tw:w-8 tw:h-8 concierge-header-expand"
              style={{ padding: 8 }}
              aria-label="Expand to full view"
            >
              <Maximize2 className="tw:w-4 tw:h-4 tw:shrink-0 concierge-maximize-icon" />
            </button>
          )}
          {showNewConversation && onNewConversationClick && (
            <button
              onClick={onNewConversationClick}
              className="tw:flex tw:items-center tw:justify-center tw:rounded-lg tw:transition-colors tw:cursor-pointer tw:border-none tw:bg-transparent tw:group tw:w-8 tw:h-8"
              style={{ padding: 8 }}
              aria-label="Create new conversation"
            >
              <span className="tw:flex tw:items-center tw:justify-center tw:w-4 tw:h-4 concierge-new-conversation-icon [&>svg]:tw:block">
                {newConversationIcon ?? (
                  <MessageCirclePlus className="tw:w-4 tw:h-4 tw:shrink-0" />
                )}
              </span>
            </button>
          )}
        </div>

        {/* Center title - flex item so it aligns with icons */}
        <span
          data-title-text
          className="tw:flex tw:items-center tw:justify-center"
          style={{
            fontSize: `${state.titleFontSize}px`,
            fontWeight: getFontWeightValue(state.titleFontWeight),
            fontFamily: fontFamilyCss !== "inherit" ? fontFamilyCss : undefined,
            color: resolveColor(state.titleColor),
            letterSpacing: `${state.titleLetterSpacing}px`,
            textTransform: state.titleTextTransform,
            textAlign: "center",
            lineHeight: 1,
            flex: 1,
            minWidth: 0,
          }}
        >
          {/* A title that does not fit ends in "…", so the buttons keep their room. */}
          <span
            style={{
              minWidth: 0,
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
              lineHeight: 1.25,
            }}
          >
            {state.titleContent}
          </span>
          {titleAddon}
        </span>

        {/* Right side buttons - fixed width to balance with left side */}
        <div
          className="tw:flex tw:items-center tw:gap-1 tw:justify-end tw:flex-shrink-0 concierge-header-side"
          style={{ minWidth: "80px" }}
        >
          <button
            onClick={onClose}
            className="tw:flex tw:items-center tw:justify-center tw:rounded-lg tw:transition-colors tw:cursor-pointer tw:border-none tw:bg-transparent tw:group tw:w-8 tw:h-8"
            style={{ padding: 8 }}
            aria-label="Close"
          >
            <CloseIcon className="tw:w-4 tw:h-4 tw:shrink-0 concierge-close-icon" />
          </button>
        </div>
      </div>
      {bottomContent}
    </div>
  );

  // Wrap with WidgetWrapper for edit capability
  return (
    <WidgetWrapper
      widgetId={widgetId}
      displayName={displayName}
      editFields={editFields}
      editValues={state}
      onEditChange={handleEditChange}
      onSave={canSave ? handleSave : undefined}
      isSaving={isSaving}
      themeColors={themeColors}
      themeFonts={themeFonts}
    >
      {widgetContent}
    </WidgetWrapper>
  );
};
